import { useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Separator } from "./ui/separator";
import {
  LayoutDashboard, Car, Users, Calendar, Receipt, Wrench,
  Bell, FileText, BarChart3, ScrollText, LogOut, Menu, ChevronRight, Building2, ShieldCheck, Wallet, Shield, Package
} from "lucide-react";

const menuConfig = {
  admin: [
    { label: "Tableau de bord", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Administrateurs", icon: Shield, path: "/administrateurs" },
    { label: "Gestionnaires", icon: ShieldCheck, path: "/gestionnaires" },
    { label: "Investisseurs", icon: Building2, path: "/investisseurs" },
    { label: "Vehicules", icon: Car, path: "/vehicules" },
    { label: "Chauffeurs", icon: Users, path: "/chauffeurs" },
    { label: "Planning", icon: Calendar, path: "/planning" },
    { label: "Recettes", icon: Receipt, path: "/recettes" },
    { label: "Dettes", icon: Wallet, path: "/dettes" },
    { label: "Reparations", icon: Wrench, path: "/reparations" },
    { label: "Pieces", icon: Package, path: "/pieces" },
    { label: "Alertes", icon: Bell, path: "/alertes" },
    { label: "Devis", icon: FileText, path: "/devis" },
    { label: "Bilans", icon: BarChart3, path: "/bilans" },
    { label: "Logs", icon: ScrollText, path: "/logs" },
  ],
  gestionnaire: [
    { label: "Tableau de bord", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Vehicules", icon: Car, path: "/vehicules" },
    { label: "Investisseurs", icon: Building2, path: "/investisseurs" },
    { label: "Chauffeurs", icon: Users, path: "/chauffeurs" },
    { label: "Planning", icon: Calendar, path: "/planning" },
    { label: "Recettes", icon: Receipt, path: "/recettes" },
    { label: "Dettes", icon: Wallet, path: "/dettes" },
    { label: "Reparations", icon: Wrench, path: "/reparations" },
    { label: "Pieces", icon: Package, path: "/pieces" },
    { label: "Alertes", icon: Bell, path: "/alertes" },
    { label: "Devis", icon: FileText, path: "/devis" },
  ],
  investisseur: [
    { label: "Tableau de bord", icon: LayoutDashboard, path: "/investisseur" },
    { label: "Recettes", icon: Receipt, path: "/investisseur/recettes" },
    { label: "Reparations", icon: Wrench, path: "/investisseur/reparations" },
    { label: "Pieces", icon: Package, path: "/investisseur/pieces" },
    { label: "Devis", icon: FileText, path: "/devis" },
    { label: "Bilans", icon: BarChart3, path: "/bilans" },
  ],
  chauffeur: [
    { label: "Mes dettes", icon: Receipt, path: "/chauffeur-portal" },
  ],
};

function SidebarContent({ items, currentPath, onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#0F52BA] flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white font-heading tracking-tight">VTC Fleet</h1>
            <p className="text-[11px] text-slate-400">Gestion de flotte</p>
          </div>
        </div>
      </div>
      <Separator className="bg-slate-700/50" />
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const active = currentPath === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`sidebar-link ${active ? "active" : "text-slate-300"}`}
            >
              <Icon className="w-[18px] h-[18px]" />
              <span>{item.label}</span>
              {active && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = menuConfig[user?.role] || [];
  const initials = user ? `${(user.prenom || "")[0] || ""}${(user.nom || "")[0] || ""}`.toUpperCase() : "";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className="w-60 bg-slate-900 text-white hidden md:block fixed h-full z-30" data-testid="sidebar">
        <SidebarContent items={items} currentPath={location.pathname} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-60 p-0 bg-slate-900 border-none">
          <SidebarContent items={items} currentPath={location.pathname} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 md:ml-60">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-3 md:px-8 h-14 flex items-center justify-between" data-testid="header">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="md:hidden shrink-0" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 md:hidden">
              <div className="w-7 h-7 rounded-md bg-[#0F52BA] flex items-center justify-center">
                <Car className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-bold font-heading text-slate-900">VTCFleet</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-900">{user?.prenom} {user?.nom}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
            <Avatar className="w-8 h-8 bg-[#0F52BA]" data-testid="user-avatar">
              <AvatarFallback className="bg-[#0F52BA] text-white text-xs font-medium">{initials}</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-slate-500 hover:text-red-600">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8 animate-slide-in" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
